import { SHORTENED_ERROR_MESSAGES } from '@/enums';
import { CustomError } from '@/utils';
import { notification } from 'antd';
import { getI18n } from 'react-i18next';

// Kept free of Order form/schema imports so it can be used from
// src/queries/order.query.ts without creating an import cycle.

const PRODUCTS_OUT_OF_STOCK_NOTIFICATION_KEY = 'PRODUCTS_OUT_OF_STOCK';

type OutOfStockProduct = {
  id?: string;
  name?: string;
  requiredQuantity?: number;
  availableQuantity?: number;
  // Products this one is a component of - names or { name } objects.
  componentOf?: (string | { name?: string } | null)[];
};

const getErrorResponseData = (error: unknown) =>
  (error as CustomError | undefined)?.response?.data;

/**
 * Backend 409 raised by POST/PUT /orders when products don't have enough
 * stock. `details.products` lists each short product with how many the
 * order needs vs. has left; `details.productNames` is a joined fallback.
 */
export const isProductsOutOfStockError = (error: unknown): boolean =>
  getErrorResponseData(error)?.errorCode ===
  SHORTENED_ERROR_MESSAGES.PRODUCTS_OUT_OF_STOCK;

const getComponentOfNames = (componentOf: OutOfStockProduct['componentOf']) =>
  (componentOf ?? [])
    .map(parent => (typeof parent === 'string' ? parent : parent?.name))
    .filter((name): name is string => !!name);

/**
 * One line per out-of-stock product ("Test More: needs 1, 0 available"),
 * plus which products it's a component of, so the user knows exactly what
 * to lower.
 */
export const notifyProductsOutOfStock = (error: unknown) => {
  const i18n = getI18n();
  const details = getErrorResponseData(error)?.details ?? {};
  const products: OutOfStockProduct[] = Array.isArray(details.products)
    ? details.products
    : [];
  const productNames =
    typeof details.productNames === 'string' ? details.productNames : '';

  notification.error({
    key: PRODUCTS_OUT_OF_STOCK_NOTIFICATION_KEY,
    message: i18n.t('order.productsOutOfStock.title'),
    description: products.length ? (
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {products.map((product, index) => {
          const componentOfNames = getComponentOfNames(product.componentOf);

          return (
            <li key={product.id ?? index}>
              {i18n.t('order.productsOutOfStock.productLine', {
                name:
                  product.name || i18n.t('order.invoicePricingUnknownProduct'),
                required: product.requiredQuantity ?? 0,
                available: Math.max(product.availableQuantity ?? 0, 0),
              })}
              {componentOfNames.length > 0 &&
                ` ${i18n.t('order.productsOutOfStock.componentOf', {
                  names: componentOfNames.join(', '),
                })}`}
            </li>
          );
        })}
      </ul>
    ) : (
      productNames || undefined
    ),
  });
};
